import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star } from "lucide-react";
import api from "@/api";
import { useLang } from "@/i18n";

export default function ReviewPage() {
  const { token } = useParams();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/reviews/${token}`).then((r) => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post(`/reviews/${token}`, { rating, message });
      setDone(true);
    } catch (err) {
      setError(err?.response?.status === 409 ? t.review.already : t.review.error);
    } finally {
      setSending(false);
    }
  };

  const wrap = (children, testid) => (
    <main data-testid={testid} className="flex min-h-screen items-center justify-center bg-ivoire px-5 pb-24 pt-32">
      <div className="w-full max-w-xl rounded-md border border-maquis-deep/10 bg-casse p-8 sm:p-12 text-center">
        {children}
      </div>
    </main>
  );

  if (invalid) {
    return wrap(
      <>
        <p data-testid="review-invalid" className="font-serif text-2xl text-maquis-deep">{t.review.invalid}</p>
        <Link to="/" data-testid="review-home-link" className="btn-outline-elegant mt-8 inline-block text-maquis-deep hover:bg-maquis-deep hover:text-casse">
          {t.review.home}
        </Link>
      </>,
      "review-invalid-page"
    );
  }

  if (!info) {
    return <main className="flex min-h-screen items-center justify-center bg-ivoire pt-20 text-maquis-soft">{t.auth.checking}</main>;
  }

  if (done || info.submitted) {
    return wrap(
      <>
        <h1 data-testid="review-success-title" className="font-serif text-3xl text-maquis-deep">
          {done ? t.review.success_title : t.review.already}
        </h1>
        {done && <p className="mt-4 text-sm leading-relaxed text-maquis-olive">{t.review.success_text}</p>}
        <Link to="/" data-testid="review-home-link" className="btn-outline-elegant mt-8 inline-block text-maquis-deep hover:bg-maquis-deep hover:text-casse">
          {t.review.home}
        </Link>
      </>,
      "review-done-page"
    );
  }

  return wrap(
    <>
      <p className="eyebrow">{t.brand}</p>
      <h1 data-testid="review-title" className="mt-3 font-serif text-3xl sm:text-4xl text-maquis-deep">{t.review.title}</h1>
      <p className="mt-3 text-sm leading-relaxed text-maquis-olive">{t.review.sub}</p>
      {(info.property_name || info.arrival) && (
        <p data-testid="review-stay-summary" className="mt-5 rounded-sm bg-ivoire px-4 py-3 text-xs uppercase tracking-[0.15em] text-maquis-soft">
          {[info.property_name, info.arrival && info.departure ? `${info.arrival} → ${info.departure}` : null].filter(Boolean).join(" · ")}
        </p>
      )}
      <form onSubmit={submit} data-testid="review-form" className="mt-8 space-y-6 text-left">
        <div>
          <label className="field-label">{t.review.rating}</label>
          <div className="flex gap-2" data-testid="review-rating-input">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" data-testid={`review-star-${s}`} onClick={() => setRating(s)} aria-label={`${s}/5`}>
                <Star size={26} strokeWidth={1.25} className={s <= rating ? "fill-[#B08D57] text-[#B08D57]" : "text-sable"} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="field-label">{t.review.message}</label>
          <textarea
            rows={4}
            data-testid="review-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="field-input resize-y"
          />
        </div>
        {error && <p data-testid="review-error" className="text-sm text-[#B08D57]">{error}</p>}
        <button
          type="submit"
          disabled={sending}
          data-testid="review-submit"
          className="btn-outline-elegant w-full text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-50"
        >
          {sending ? t.review.sending : t.review.submit}
        </button>
      </form>
    </>,
    "review-page"
  );
}
